import { ChevronRightIcon } from '@repo/studio-design-system-ui-icons/arrows/chevron-right'
import { CheckIcon } from '@repo/studio-design-system-ui-icons/general/check'
import { Slot } from 'primitives/slot'
import * as React from 'react'
import { StudioLayoutLink } from '@repo/studio-config/studio-config-router'
import { tw } from 'utils/classnames'
import invariant from 'tiny-invariant'

import { Switch } from '@repo/studio-components-switch'
import { ShimmerWrapper } from '@repo/studio-design-system-ui-shimmer-wrapper'

interface MenuRowProps extends React.ComponentPropsWithoutRef<'button'> {
  variant?:
    | 'lefticon-shortcut'
    | 'lefticon-shortcut-aaa'
    | 'left-icon'
    | 'second-position'
    | 'text-only'
    | 'link-icon'
    // used with .Toggle
    // it renders a label so _watch out_ for sending button specific props
    | 'toggle-row'
    | 'popover'
    | 'slim'
    | 'flow-shape'
  selected?: boolean
  asChild?: boolean
}

const MenuRowRoot = function MenuRowRoot({
  ref,
  variant,
  selected,
  asChild,
  ...props
}: MenuRowProps & {
  ref?: React.Ref<HTMLButtonElement>
}) {
  // TS has to make TS happy :grimacing:
  // oxlint-disable-next-line no-nested-ternary
  const Comp = asChild ? Slot : variant === 'toggle-row' ? ('label' as 'button') : 'button'

  // if selected is controlled, variant cannot be toggle row
  const isSelectedUncontrolled = typeof selected === 'undefined'
  invariant(isSelectedUncontrolled || variant !== 'toggle-row', 'Toggle row cannot be selected')

  return (
    <Comp
      type={asChild ? undefined : 'button'}
      {...props}
      className={tw(
        'flex w-full shrink-0 items-center justify-start text-left text-theme_semantic_surface_ui_foreground-color-subtle hover:text-theme_semantic_surface_ui_foreground-color-subtle gap-3',
        'rounded-2 min-h-[40px] cursor-pointer pl-4 outline-none focus-visible:ring-2 focus-visible:ring-theme_semantic_action_focus-ring-outer-color',
        selected
          ? tw(
              'text-theme_semantic_surface_ui_foreground-color-strong hover:text-theme_semantic_surface_ui_foreground-color-strong bg-theme_semantic_action_primary_background-color-selected',
              variant === 'lefticon-shortcut-aaa' &&
                'bg-theme_semantic_action_primary_background-color-active',
              variant === 'slim' &&
                'bg-theme_semantic_action_ghost_background-color-active text-theme_semantic_surface_ui_foreground-color-regular hover:text-theme_semantic_surface_ui_foreground-color-regular',
              variant === 'flow-shape' && 'text-violet-500',
            )
          : 'hover:bg-theme_semantic_action_ghost_background-color-hover',
        variant === 'lefticon-shortcut' && 'justify-between',
        variant === 'lefticon-shortcut-aaa' && 'justify-between pl-2',
        variant === 'left-icon' && 'pl-2',
        variant === 'second-position' && 'pl-[43px]',
        variant === 'text-only' && 'px-4',
        variant === 'link-icon' && 'pl-2',
        variant === 'toggle-row' && 'justify-between px-2',
        variant === 'popover' && 'px-2',
        variant === 'slim' &&
          'text-theme_semantic_surface_ui_foreground-color-regular p-2 min-h-0 max-h-[32px] hover:bg-theme_semantic_action_ghost_background-color-hover [&_svg]:size-4 justify-start group/row',
        variant === 'flow-shape' &&
          'flex-col items-center gap-1.5 p-2 min-h-0 w-auto rounded-2 text-center typography-12 hover:bg-gray-800 group/flow-shape bg-transparent',
        props.className,
      )}
      ref={ref}
    />
  )
}

function MenuRowIcon({ ...props }: React.ComponentProps<'svg'>) {
  const Comp = Slot as unknown as 'svg'

  return (
    <Comp
      {...props}
      className={tw(
        'text-theme_semantic_surface_ui_foreground-color-subtle shrink-0 size-6',
        props.className,
      )}
    />
  )
}

function MenuRowRightIcon(props: React.ComponentProps<'svg'>) {
  const Comp = Slot as unknown as 'svg'

  return <Comp {...props} className={tw('ml-auto mr-2 h-4 w-4 shrink-0', props.className)} />
}

function MenuRowArrow(props: React.ComponentProps<'svg'>) {
  return <ChevronRightIcon {...props} className={tw('ml-2 mr-3 h-4 w-[17px]', props.className)} />
}

function MenuRowCheck(props: React.ComponentProps<'svg'>) {
  return (
    <CheckIcon
      {...props}
      className={tw(
        'ml-2 mr-4 h-6 w-6 text-theme_semantic_surface_ui_foreground-color-faint',
        props.className,
      )}
    />
  )
}

function MenuRowLink({
  href,
  asChild: asChildProp,
  ...props
}: React.ComponentPropsWithoutRef<'a'> & { href?: string; asChild?: boolean }) {
  const className = tw('flex h-full w-full items-center outline-none', props.className)
  if (asChildProp) {
    return <Slot {...props} className={className} />
  }
  return (
    <StudioLayoutLink {...props} className={className} to={href || '#'}>
      {props.children}
    </StudioLayoutLink>
  )
}

function MenuRowToggle(props: React.ComponentProps<typeof Switch>) {
  return <Switch {...props} />
}

function MenuRowSkeleton({
  className,
  variant,
}: {
  className?: string
  variant?: MenuRowProps['variant']
}) {
  return (
    <ShimmerWrapper className={tw('rounded-2 block w-full', className)}>
      <div
        className={tw(
          'rounded-2 w-full h-10 bg-theme_semantic_surface_skeleton_outer',
          variant === 'slim' && 'h-8',
        )}
      />
    </ShimmerWrapper>
  )
}

function MenuRowLabel(props: React.ComponentProps<'span'>) {
  return (
    <span
      {...props}
      className={tw(
        'flex-1 min-w-0 truncate text-theme_semantic_surface_ui_foreground-color-regular',
        props.className,
      )}
    />
  )
}

function MenuRowIndicator(props: React.ComponentProps<'svg'>) {
  return (
    <ChevronRightIcon
      {...props}
      className={tw(
        'shrink-0 min-w-4 min-h-4 text-theme_semantic_surface_ui_foreground-color-faint transition-transform duration-200 ease-out',
        'group-hover/row:translate-x-1',
        props.className,
      )}
    />
  )
}

function MenuRowFlowShapeItem({
  value,
  selected,
  className,
}: {
  value: string | React.ReactNode
  selected?: boolean
  className?: string
}) {
  if (typeof value === 'string') {
    return (
      <img
        src={value}
        alt=""
        className={tw(
          'size-full aspect-square rounded-2 object-cover ring-0 ring-inset transition-shadow',
          'group-active/flow-shape:ring-2 group-active/flow-shape:ring-violet-500',
          selected && 'ring-2 ring-violet-500',
          className,
        )}
      />
    )
  }

  return (
    <div
      className={tw(
        'flex items-center justify-center rounded-2 size-full aspect-square bg-gray-900 text-gray-200 [&_svg]:size-6',
        'group-active/flow-shape:bg-violet-500',
        selected && 'bg-violet-500',
        className,
      )}
    >
      {value}
    </div>
  )
}

export const MenuRow = Object.assign(MenuRowRoot, {
  Icon: MenuRowIcon,
  RightIcon: MenuRowRightIcon,
  Arrow: MenuRowArrow,
  Check: MenuRowCheck,
  Link: MenuRowLink,
  Label: MenuRowLabel,
  Indicator: MenuRowIndicator,
  Toggle: MenuRowToggle,
  Skeleton: MenuRowSkeleton,
  FlowShapeItem: MenuRowFlowShapeItem,
})
